import { useTranslation } from 'react-i18next';

import { Button } from 'components/Button';
import { Headline } from 'components/Headline';
import { Select } from 'components/Select';

import { usePagination } from './Pagination.hooks';
import * as S from './Pagination.styles';

type PaginationProps = {
  canPreviousPage: boolean;
  canNextPage: boolean;
  gotoPage: (updater: number | ((pageIndex: number) => number)) => void;
  previousPage: () => void;
  nextPage: () => void;
  pageIndex: number;
  pageOptions: number[];
  pageSize: number;
  pageCount: number;
  setPageSize: (pageSize: number) => void;
};

export const Pagination = (props: PaginationProps) => {
  const {
    canPreviousPage,
    canNextPage,
    gotoPage,
    previousPage,
    nextPage,
    pageIndex,
    pageOptions,
    pageSize,
    setPageSize,
    pageCount,
  } = props;

  const {
    currentPage,
    sortingSizesMapped,
    pageOptionsMapped,
    defaultSortingSize,
    setPreviousPage,
    setNextPage,
    defaultPage,
    currentPageSet,
  } = usePagination(pageOptions, pageIndex, pageSize, previousPage, nextPage);
  const { t } = useTranslation();

  return (
    <S.Pagination>
      <S.Container>
        <Headline
          scale="s"
          fontFamily="LFT Zeno"
          color="borderColorDarkSecondary"
          textTransform="capitalize"
        >
          {t('common.view')}
        </Headline>

        <Select
          motive="filter"
          name="pageSize"
          options={sortingSizesMapped}
          defaultValue={defaultSortingSize}
          onChange={(e) => {
            setPageSize(Number(e.value));
          }}
        />

        <Headline scale="s" fontFamily="LFT Zeno" color="borderColorDarkSecondary">
          {t('common.perPage')}
        </Headline>
      </S.Container>

      <S.Container>
        <Button
          iconLeft="FiChevronLeft"
          motive="slidePage"
          scale="s"
          onClick={() => setPreviousPage()}
          disabled={!canPreviousPage || pageCount === 1}
          testID="prevBtn"
        >
          {t('common.prev')}
        </Button>

        <S.Container>
          <Headline scale="s" fontFamily="LFT Zeno" color="borderColorDarkSecondary">
            {t('common.page')}
          </Headline>

          <Select
            disabled={pageCount === 1}
            name="pageIndex"
            motive="filter"
            value={currentPage}
            options={pageOptionsMapped}
            defaultValue={defaultPage}
            onChange={(e) => {
              gotoPage(e.value);
              currentPageSet(e);
            }}
          />

          <Headline scale="s" fontFamily="LFT Zeno" color="borderColorDarkSecondary">
            {t('common.of')} {pageOptions.length}
          </Headline>
        </S.Container>

        <Button
          iconRight="FiChevronRight"
          motive="slidePage"
          scale="s"
          onClick={() => setNextPage()}
          disabled={!canNextPage || pageCount === 1}
          testID="nextBtn"
        >
          {t('common.next')}
        </Button>
      </S.Container>
    </S.Pagination>
  );
};
